import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Button, Typography, Grid, Chip, IconButton, Tooltip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Dialog, TextField, Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Barcode from "react-barcode";
import BarcodeManager from "../../../Components/BarcodeManager";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useLazyGetProductHistoryQuery } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import placeholderImage from "../../../Images/fav.png";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

type TabKey = "overview" | "inventory" | "sales" | "suppliers" | "history";

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "overview", label: "Overview", icon: <InventoryIcon sx={{ fontSize: 16 }} /> },
  { key: "inventory", label: "Inventory", icon: <StorefrontIcon sx={{ fontSize: 16 }} /> },
  { key: "sales", label: "Sales", icon: <PointOfSaleIcon sx={{ fontSize: 16 }} /> },
  { key: "suppliers", label: "Suppliers", icon: <LocalShippingIcon sx={{ fontSize: 16 }} /> },
  { key: "history", label: "History", icon: <HistoryIcon sx={{ fontSize: 16 }} /> },
];

const Index: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage } = useNotifier();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("overview");
  const [printLabel, setPrintLabel] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [triggerHistory] = useLazyGetProductHistoryQuery();

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  // Load product
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${API_URL}/product/${id}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(json => { if (json.status && json.data) setProduct(json.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Load history data (needed for all tabs — alerts, insights, suppliers)
  useEffect(() => {
    if (!id || historyData) return;
    setHistoryLoading(true);
    triggerHistory(id).unwrap()
      .then((json: any) => { if (json.data) setHistoryData(json.data); })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [id, historyData]);

  const stock = Number(product?.current_stock || 0);
  const reorderPoint = Number(product?.reorder_point || 0);
  const tracked = product?.track_inventory;
  const stockStatus = !tracked ? "untracked" : stock <= 0 ? "out" : stock <= reorderPoint ? "low" : "good";
  const stockColors = { good: { bg: "#D1FAE5", color: "#059669", label: "In Stock" }, low: { bg: "#FEF3C7", color: "#D97706", label: "Low Stock" }, out: { bg: "#FEE2E2", color: "#DC2626", label: "Out of Stock" }, untracked: { bg: "#F3F4F6", color: "#6B7280", label: "Untracked" } };
  const sc = stockColors[stockStatus];
  const barcode = product?.productManualBarcode || product?.productBarcode || "";

  if (loading) return <PageLoader />;

  if (!product) return (
    <Box sx={{ textAlign: "center", py: 10 }}>
      <Typography fontSize={16} color="#6B7280">Product not found</Typography>
      <Button onClick={() => navigate("/product/list")} sx={{ mt: 2, textTransform: "none" }}>Back to Products</Button>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>

      {/* ═══ BREADCRUMB ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ width: 28, height: 28 }}>
            <ArrowBackIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
          </IconButton>
          <Typography fontSize={12} color="#9CA3AF" sx={{ cursor: "pointer", "&:hover": { color: "#374151" } }} onClick={() => navigate("/product/list")}>Products</Typography>
          <Typography fontSize={12} color="#D1D5DB">/</Typography>
          <Typography fontSize={12} color="#374151" fontWeight={600} sx={{ textTransform: "capitalize" }}>{product.productName}</Typography>
        </Box>
      </Box>

      {/* ═══ HERO SECTION ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 3 }}>
        <Grid container spacing={3}>
          {/* Left: Image + Info */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", gap: 3 }}>
              {/* Image */}
              <Box sx={{ width: 140, height: 140, borderRadius: 3, overflow: "hidden", bgcolor: "#F9FAFB", border: "1px solid #E5E7EB", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img src={product.productImg ? `${LARAVEL_URL}/${product.productImg}` : placeholderImage} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              </Box>
              {/* Info */}
              <Box sx={{ flex: 1 }}>
                <Typography fontSize={28} fontWeight={700} color="#111827" letterSpacing="-0.02em" sx={{ textTransform: "capitalize", lineHeight: 1.2 }}>
                  {product.productName}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  {barcode && <Chip label={barcode} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#F3F4F6", color: "#374151", fontFamily: "monospace" }} />}
                  <Chip label={sc.label} size="small" icon={stockStatus === "good" ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : stockStatus === "low" ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : stockStatus === "out" ? <ErrorIcon sx={{ fontSize: 14 }} /> : undefined}
                    sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: sc.bg, color: sc.color, "& .MuiChip-icon": { color: sc.color } }} />
                  {product.isActive ? <Chip label="Active" size="small" sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#D1FAE5", color: "#059669" }} /> : <Chip label="Inactive" size="small" sx={{ height: 24, fontSize: 11, bgcolor: "#FEE2E2", color: "#DC2626" }} />}
                </Box>
                {/* Actions */}
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button size="small" variant="contained" disableElevation startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate(`/product/edit/${id}`)}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#111827", "&:hover": { bgcolor: "#1F2937" } }}>
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                    onClick={() => { setTab("overview"); setPrintLabel(true); }}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
                    Print Label
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate("/bill/create")}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
                    Order Supplier
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right: KPI Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={1.5}>
              {[
                { label: "Cost Price", value: `$${Number(product.productPrice || 0).toFixed(2)}`, icon: <AttachMoneyIcon />, color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB" },
                { label: "Wholesale", value: `$${Number(product.wholesaleprice || 0).toFixed(2)}`, icon: <StorefrontIcon />, color: "#7C3AED", bg: "#FAF5FF", border: "#EDE9FE" },
                { label: "Retail Price", value: `$${Number(product.salePrice || 0).toFixed(2)}`, icon: <PointOfSaleIcon />, color: "#059669", bg: "#F0FDF4", border: "#D1FAE5" },
                { label: "Stock", value: tracked ? String(stock) : "N/A", icon: <InventoryIcon />, color: sc.color, bg: sc.bg === "#F3F4F6" ? "#F9FAFB" : sc.bg, border: sc.bg === "#F3F4F6" ? "#E5E7EB" : sc.bg },
              ].map((kpi, i) => (
                <Grid item xs={6} key={i}>
                  <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${kpi.border}`, bgcolor: kpi.bg, transition: "all 0.15s", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.04)" } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 16, color: kpi.color } })}
                      <Typography fontSize={10} color="#9CA3AF" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em">{kpi.label}</Typography>
                    </Box>
                    <Typography fontSize={22} fontWeight={700} color={kpi.color} sx={{ fontVariantNumeric: "tabular-nums" }}>{kpi.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* ═══ BARCODE CARD ═══ */}
      {(product?.productBarcode || product?.productManualBarcode) && (
        <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {barcode && (
              <>
                <Box sx={{ "& svg rect": { fill: "#374151" }, "& svg text": { fill: "#6B7280", fontSize: "12px" } }}>
                  <Barcode value={barcode} width={1.5} height={40} displayValue={false} margin={0} />
                </Box>
                <Box>
                  <Typography fontSize={14} fontFamily="monospace" color="#374151" fontWeight={600}>{barcode}</Typography>
                  <Typography fontSize={10} color="#9CA3AF">{product.productManualBarcode ? "Manual Barcode" : "System Barcode"}</Typography>
                </Box>
                <Tooltip title="Copy barcode">
                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(barcode); showMessage("Barcode copied"); }}
                    sx={{ width: 28, height: 28, border: "1px solid #E5E7EB", borderRadius: 1.5 }}>
                    <ContentCopyIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {product.productManualBarcode && product.productBarcode && (
              <Box sx={{ ml: 2, pl: 2, borderLeft: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box>
                  <Typography fontSize={12} fontFamily="monospace" color="#6B7280" fontWeight={500}>{product.productBarcode}</Typography>
                  <Typography fontSize={10} color="#9CA3AF">System Barcode</Typography>
                </Box>
                <Tooltip title="Copy system barcode">
                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(product.productBarcode); showMessage("System barcode copied"); }}
                    sx={{ width: 24, height: 24, border: "1px solid #E5E7EB", borderRadius: 1 }}>
                    <ContentCopyIcon sx={{ fontSize: 12, color: "#9CA3AF" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ═══ TAB NAVIGATION ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3 }}>
        <Box sx={{ display: "flex", gap: 0 }}>
          {TABS.map(t => (
            <Box key={t.key} onClick={() => setTab(t.key)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 1.5, cursor: "pointer",
                borderBottom: tab === t.key ? "2px solid #111827" : "2px solid transparent",
                color: tab === t.key ? "#111827" : "#9CA3AF",
                transition: "all 0.15s",
                "&:hover": { color: "#374151" },
              }}>
              {React.cloneElement(t.icon as React.ReactElement, { sx: { fontSize: 16 } })}
              <Typography fontSize={13} fontWeight={tab === t.key ? 600 : 500}>{t.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ═══ TAB CONTENT ═══ */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Main content */}
          <Grid item xs={12} md={9}>

            {/* OVERVIEW */}
            {tab === "overview" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Product Details */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Product Details</Typography>
                  {[
                    { label: "Category", value: product.category_chain?.map((c: any) => c.name).join(" → ") || "—" },
                    { label: "Brand", value: product.brand_name || "—" },
                    { label: "Unit", value: (() => {
                        const baseLabel = product.base_unit_name
                          ? `${product.base_unit_name}${product.base_unit_abbreviation ? ` (${product.base_unit_abbreviation})` : ""}`
                          : "";
                        const purchaseLabel = product.purchase_unit_name
                          ? `${product.purchase_unit_name}${product.purchase_unit_abbreviation ? ` (${product.purchase_unit_abbreviation})` : ""}`
                          : "";
                        if (baseLabel && purchaseLabel && baseLabel !== purchaseLabel) {
                          const rate = product.conversion_rate ? ` · 1 ${product.purchase_unit_abbreviation || product.purchase_unit_name} = ${product.conversion_rate} ${product.base_unit_abbreviation || product.base_unit_name}` : "";
                          return `${baseLabel} / ${purchaseLabel}${rate}`;
                        }
                        if (baseLabel) return baseLabel;
                        if (purchaseLabel) return purchaseLabel;
                        const legacy = Array.isArray(product.productConversionUnit)
                          ? product.productConversionUnit.join(", ")
                          : (typeof product.productConversionUnit === "string" && product.productConversionUnit
                              ? (() => { try { return JSON.parse(product.productConversionUnit).join(", "); } catch { return product.productConversionUnit; } })()
                              : "");
                        return legacy || "—";
                      })() },
                    { label: "Online", value: product.productViewOnline ? "Yes" : "No" },
                    { label: "POS", value: product.productViewPos ? "Yes" : "No" },
                    { label: "Tax Group", value: product.tax_name ? `${product.tax_name} (${product.tax_rate}%)` : "None" },
                    { label: "Weight-Based", value: product.is_weight_based ? "Yes" : "No" },
                    { label: "Qty Prompt", value: product.pos_requires_quantity ? "Yes" : "No" },
                  ].map((row, i, arr) => (
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 1.25, borderBottom: i < arr.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                      <Typography fontSize={13} color="#6B7280">{row.label}</Typography>
                      <Typography fontSize={13} color="#111827" fontWeight={500} sx={{ textTransform: "capitalize" }}>{row.value}</Typography>
                    </Box>
                  ))}
                  {product.productShortDescription && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #F3F4F6" }}>
                      <Typography fontSize={12} color="#9CA3AF" mb={0.5}>Description</Typography>
                      <Typography fontSize={13} color="#374151" dangerouslySetInnerHTML={{ __html: product.productShortDescription }} />
                    </Box>
                  )}
                </Box>

                {/* Barcode Manager */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <BarcodeManager productId={String(id)} productName={product.productName} price={Number(product.salePrice || 0)} autoOpenPrint={printLabel} onAutoOpenHandled={() => setPrintLabel(false)} />
                </Box>
              </Box>
            )}

            {/* INVENTORY */}
            {tab === "inventory" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2.5}>Stock Levels</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { label: "Current Stock", value: stock, color: "#111827" },
                      { label: "Reorder Point", value: reorderPoint, color: "#D97706" },
                      { label: "Reorder Qty", value: Number(product.reorder_qty || 0), color: "#7C3AED" },
                      { label: "Min Stock", value: Number(product.minimum_stock || 0), color: "#DC2626" },
                    ].map((s, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, border: "1px solid #E5E7EB" }}>
                          <Typography fontSize={10} color="#9CA3AF" textTransform="uppercase" fontWeight={600}>{s.label}</Typography>
                          <Typography fontSize={24} fontWeight={700} color={s.color}>{s.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  {tracked && reorderPoint > 0 && (
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                        <Typography fontSize={12} color="#6B7280">Stock Level</Typography>
                        <Typography fontSize={12} fontWeight={600} color={sc.color}>{Math.min(100, Math.round((stock / (reorderPoint * 2)) * 100))}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={Math.min(100, Math.round((stock / (reorderPoint * 2)) * 100))}
                        sx={{ height: 8, borderRadius: 4, bgcolor: "#F3F4F6", "& .MuiLinearProgress-bar": { bgcolor: sc.color, borderRadius: 4 } }} />
                      {stock <= reorderPoint && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#FEF3C7", border: "1px solid #FDE68A", display: "flex", alignItems: "center", gap: 1 }}>
                          <WarningAmberIcon sx={{ fontSize: 16, color: "#D97706" }} />
                          <Typography fontSize={12} color="#92400E" fontWeight={600}>Stock is below reorder point. Consider placing a purchase order.</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Stock Movements */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Stock Movements</Typography>
                  {historyLoading ? <CircularProgress size={20} /> : historyData?.movements?.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 350 }}>
                      <Table size="small" stickyHeader>
                        <TableHead><TableRow>
                          {["Date", "Type", "Qty", "After", "Reference"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280", bgcolor: "#F9FAFB" }}>{h}</TableCell>
                          ))}
                        </TableRow></TableHead>
                        <TableBody>
                          {historyData.movements.map((m: any) => (
                            <TableRow key={m.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                              <TableCell sx={{ fontSize: 12 }}>{new Date(m.date).toLocaleDateString()}</TableCell>
                              <TableCell><Chip label={m.type.replace(/_/g, " ")} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: m.qty > 0 ? "#D1FAE5" : "#FEE2E2", color: m.qty > 0 ? "#059669" : "#DC2626", textTransform: "capitalize" }} /></TableCell>
                              <TableCell><Typography fontSize={13} fontWeight={700} color={m.qty > 0 ? "#059669" : "#DC2626"}>{m.qty > 0 ? "+" : ""}{m.qty}</Typography></TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{m.stock_after}</TableCell>
                              <TableCell sx={{ fontSize: 11, color: "#9CA3AF" }}>{m.reference || m.notes || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography fontSize={13} color="#9CA3AF" py={3} textAlign="center">No stock movements</Typography>}
                </Box>
              </Box>
            )}

            {/* SALES */}
            {tab === "sales" && (
              <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Sales Overview</Typography>
                {historyLoading ? <CircularProgress size={20} /> : (
                  <Grid container spacing={2}>
                    {[
                      { label: "Total Sold", value: historyData?.ai_insights?.total_sold || 0, color: "#059669" },
                      { label: "Total Purchased", value: historyData?.ai_insights?.total_purchased || 0, color: "#3B82F6" },
                      { label: "Avg Purchase Price", value: `$${(historyData?.ai_insights?.avg_purchase_price || 0).toFixed(2)}`, color: "#D97706" },
                      { label: "Days of Stock", value: historyData?.ai_insights?.days_of_stock ?? "N/A", color: "#7C3AED" },
                    ].map((s, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, border: "1px solid #E5E7EB" }}>
                          <Typography fontSize={10} color="#9CA3AF" textTransform="uppercase" fontWeight={600}>{s.label}</Typography>
                          <Typography fontSize={22} fontWeight={700} color={s.color}>{s.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* SUPPLIERS */}
            {tab === "suppliers" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Linked Suppliers</Typography>
                  {historyLoading ? <CircularProgress size={20} /> : historyData?.suppliers?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                          {["Supplier", "Price", "Unit", "Lead Time", "Min Order", "Default"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>{h}</TableCell>
                          ))}
                        </TableRow></TableHead>
                        <TableBody>
                          {historyData.suppliers.map((s: any, i: number) => (
                            <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                              <TableCell><Typography fontSize={13} fontWeight={600} color="#111827">{s.supplier_name}</Typography></TableCell>
                              <TableCell><Typography fontSize={14} fontWeight={700}>${(s.supplier_price || 0).toFixed(2)}</Typography></TableCell>
                              <TableCell><Chip label={s.supplier_unit || "—"} size="small" sx={{ height: 22, fontSize: 10, bgcolor: "#F3F4F6" }} /></TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{s.lead_time_days ? `${s.lead_time_days}d` : "—"}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{s.minimum_order_qty || "—"}</TableCell>
                              <TableCell>{s.is_default ? <Chip label="Default" size="small" sx={{ height: 20, fontSize: 9, bgcolor: "#FEF3C7", color: "#D97706" }} /> : "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography fontSize={13} color="#9CA3AF" py={3} textAlign="center">No suppliers linked</Typography>}
                </Box>
              </Box>
            )}

            {/* HISTORY */}
            {tab === "history" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* AI Insights */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Chip label="AI" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#EDE9FE", color: "#7C3AED" }} />
                    <Typography fontSize={15} fontWeight={600} color="#111827">Product Insights</Typography>
                  </Box>
                  {historyLoading ? <CircularProgress size={20} /> : (
                    <Grid container spacing={1.5}>
                      {[
                        { label: "Purchased", value: historyData?.ai_insights?.total_purchased || 0, color: "#3B82F6" },
                        { label: "Sold", value: historyData?.ai_insights?.total_sold || 0, color: "#059669" },
                        { label: "Avg Price", value: `$${(historyData?.ai_insights?.avg_purchase_price || 0).toFixed(2)}`, color: "#D97706" },
                        { label: "PO Count", value: historyData?.ai_insights?.purchase_count || 0, color: "#7C3AED" },
                        { label: "Suppliers", value: historyData?.ai_insights?.supplier_count || 0, color: "#0891B2" },
                        { label: "Days Stock", value: historyData?.ai_insights?.days_of_stock ?? "—", color: "#111827" },
                      ].map((c, i) => (
                        <Grid item xs={4} sm={2} key={i}>
                          <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid #E5E7EB", textAlign: "center" }}>
                            <Typography fontSize={9} color="#9CA3AF" textTransform="uppercase" fontWeight={600}>{c.label}</Typography>
                            <Typography fontSize={18} fontWeight={700} color={c.color}>{c.value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>

                {/* Activity Timeline */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Activity Timeline</Typography>
                  {historyLoading ? <CircularProgress size={20} /> :
                    historyData?.activities?.length > 0 ? (
                      <Box sx={{ position: "relative", pl: 2.5, "&::before": { content: '""', position: "absolute", left: 8, top: 0, bottom: 0, width: 2, bgcolor: "#E5E7EB" } }}>
                        {(() => {
                          const groups: Record<string, any[]> = {};
                          historyData.activities.forEach((a: any) => {
                            const day = a.date ? new Date(a.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";
                            (groups[day] ||= []).push(a);
                          });
                          return Object.entries(groups).map(([day, items]) => (
                            <Box key={day} sx={{ mb: 2.5 }}>
                              <Typography fontSize={11} fontWeight={700} color="#374151" mb={1} textTransform="uppercase" letterSpacing={0.4}>{day}</Typography>
                              {items.map((a, i) => {
                                const tone =
                                  a.type === "created" || a.type === "imported" ? "#059669" :
                                  a.type === "deleted" ? "#DC2626" :
                                  a.type?.startsWith("barcode") ? "#0891B2" :
                                  a.type === "field_changed" ? "#7C3AED" : "#3B82F6";
                                return (
                                  <Box key={i} sx={{ position: "relative", pl: 2, mb: 1.25 }}>
                                    <Box sx={{ position: "absolute", left: -23, top: 5, width: 12, height: 12, borderRadius: "50%", bgcolor: tone, border: "2px solid #fff", boxShadow: "0 0 0 2px " + tone + "40" }} />
                                    <Typography fontSize={12.5} color="#111827" fontWeight={500}>
                                      {a.description || a.type}
                                    </Typography>
                                    {(a.old_value || a.new_value) && (
                                      <Typography fontSize={11} color="#6B7280" mt={0.25}>
                                        {a.old_value ? `${a.old_value} → ` : ""}<span style={{ color: tone, fontWeight: 600 }}>{a.new_value ?? ""}</span>
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ));
                        })()}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <HistoryIcon sx={{ fontSize: 36, color: "#D1D5DB", mb: 1 }} />
                        <Typography fontSize={13} color="#9CA3AF">No activity yet for this product.</Typography>
                        <Typography fontSize={11} color="#D1D5DB" mt={0.5}>Edits, barcode changes, and stock movements will appear here automatically.</Typography>
                      </Box>
                    )
                  }
                </Box>

                {/* Stock Movements */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Stock Movements</Typography>
                  {historyLoading ? <CircularProgress size={20} /> : historyData?.movements?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                          {["Date", "Type", "Qty", "Before", "After", "Reference"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>{h}</TableCell>
                          ))}
                        </TableRow></TableHead>
                        <TableBody>
                          {historyData.movements.map((m: any) => {
                            const isIn = ["procurement", "manual_add", "return"].includes(m.type);
                            return (
                              <TableRow key={m.id}>
                                <TableCell sx={{ fontSize: 11, color: "#6B7280" }}>{m.date ? new Date(m.date).toLocaleString() : "—"}</TableCell>
                                <TableCell><Chip size="small" label={m.type} sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: isIn ? "#D1FAE5" : "#FEE2E2", color: isIn ? "#059669" : "#DC2626" }} /></TableCell>
                                <TableCell sx={{ fontSize: 13, fontWeight: 700, color: isIn ? "#059669" : "#DC2626" }}>{isIn ? "+" : "−"}{Math.abs(m.qty)}</TableCell>
                                <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{m.stock_before}</TableCell>
                                <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{m.stock_after}</TableCell>
                                <TableCell sx={{ fontSize: 11, color: "#9CA3AF" }}>{m.reference || m.notes || "—"}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography fontSize={13} color="#9CA3AF" py={3} textAlign="center">No stock movements yet.</Typography>
                  )}
                </Box>

                {/* Purchase History */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Purchase History</Typography>
                  {historyLoading ? <CircularProgress size={20} /> : historyData?.purchases?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                          {["PO #", "Supplier", "Date", "Qty", "Price", "Total"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>{h}</TableCell>
                          ))}
                        </TableRow></TableHead>
                        <TableBody>
                          {historyData.purchases.map((p: any, i: number) => (
                            <TableRow key={i} sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F9FAFB" } }} onClick={() => navigate(`/bill-history/${p.po_id}`)}>
                              <TableCell><Typography fontSize={12} fontWeight={600} color="#3B82F6">{p.po_number}</Typography></TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{p.supplier}</TableCell>
                              <TableCell sx={{ fontSize: 11, color: "#6B7280" }}>{p.date ? new Date(p.date).toLocaleDateString() : "—"}</TableCell>
                              <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{p.qty}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>${(p.price || 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>${(p.total || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography fontSize={13} color="#9CA3AF" py={3} textAlign="center">No purchase history yet.</Typography>}
                </Box>

                {/* Sales History */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 3 }}>
                  <Typography fontSize={15} fontWeight={600} color="#111827" mb={2}>Sales History</Typography>
                  {historyLoading ? <CircularProgress size={20} /> : historyData?.sales?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                          {["Date", "Invoice", "Channel", "Qty", "Price", "Total", "Customer"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>{h}</TableCell>
                          ))}
                        </TableRow></TableHead>
                        <TableBody>
                          {historyData.sales.map((s: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontSize: 11, color: "#6B7280" }}>{s.date ? new Date(s.date).toLocaleDateString() : "—"}</TableCell>
                              <TableCell><Typography fontSize={12} fontWeight={600} color="#3B82F6">{s.invoice_no || `#${s.invoice_id}`}</Typography></TableCell>
                              <TableCell><Chip size="small" label={s.channel} sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: s.channel === "POS" ? "#FEF3C7" : "#DBEAFE", color: s.channel === "POS" ? "#D97706" : "#1D4ED8" }} /></TableCell>
                              <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{s.qty}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>${(s.price || 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>${(s.total || 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{s.customer || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography fontSize={13} color="#9CA3AF" py={3} textAlign="center">No sales recorded yet.</Typography>}
                </Box>
              </Box>
            )}

          </Grid>

          {/* ═══ RIGHT PANEL — AI + ACTIONS ═══ */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: "sticky", top: 80, display: "flex", flexDirection: "column", gap: 2 }}>

              {/* AI Insights */}
              <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="AI" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 800, bgcolor: "#EDE9FE", color: "#7C3AED" }} />
                  <Typography fontSize={12} fontWeight={600} color="#111827">Insights</Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  {(() => {
                    const ai = historyData?.ai_insights;
                    const daysLeft = ai?.days_of_stock;
                    const totalSold = ai?.total_sold || 0;
                    const totalPurchased = ai?.total_purchased || 0;
                    const avgPrice = ai?.avg_purchase_price || 0;
                    const suggestedReorder = tracked && reorderPoint > 0 ? Math.max(0, Math.ceil((reorderPoint * 2) - stock)) : null;

                    return (
                      <>
                        {/* Depletion prediction */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: daysLeft && daysLeft < 7 ? "#FEF2F2" : "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {daysLeft && daysLeft < 7 ? <WarningAmberIcon sx={{ fontSize: 16, color: "#DC2626" }} /> : <CheckCircleIcon sx={{ fontSize: 16, color: "#059669" }} />}
                          </Box>
                          <Box>
                            <Typography fontSize={11} color="#9CA3AF">Stock lasts</Typography>
                            <Typography fontSize={16} fontWeight={700} color={daysLeft && daysLeft < 7 ? "#DC2626" : "#059669"}>
                              {daysLeft ? `${daysLeft} days` : "N/A"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Demand trend */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <TrendingUpIcon sx={{ fontSize: 16, color: "#3B82F6" }} />
                          </Box>
                          <Box>
                            <Typography fontSize={11} color="#9CA3AF">Demand</Typography>
                            <Typography fontSize={14} fontWeight={600} color="#3B82F6">
                              {totalSold > 0 ? `${totalSold} units sold` : "No sales data"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Suggested reorder */}
                        {suggestedReorder !== null && suggestedReorder > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "#FAF5FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <LocalShippingIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
                            </Box>
                            <Box>
                              <Typography fontSize={11} color="#9CA3AF">Suggested reorder</Typography>
                              <Typography fontSize={14} fontWeight={600} color="#7C3AED">{suggestedReorder} units</Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Avg price */}
                        {avgPrice > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <AttachMoneyIcon sx={{ fontSize: 16, color: "#D97706" }} />
                            </Box>
                            <Box>
                              <Typography fontSize={11} color="#9CA3AF">Avg purchase price</Typography>
                              <Typography fontSize={14} fontWeight={600} color="#D97706">${avgPrice.toFixed(2)}</Typography>
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>

              {/* Smart Actions */}
              <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 2.5 }}>
                <Typography fontSize={12} fontWeight={600} color="#111827" mb={1.5}>Smart Actions</Typography>
                {[
                  { label: "Auto Reorder", icon: <LocalShippingIcon sx={{ fontSize: 14 }} />, color: "#7C3AED", bg: "#FAF5FF", onClick: () => navigate("/bill/create"), show: tracked && stock <= reorderPoint },
                  { label: "Edit Product", icon: <EditIcon sx={{ fontSize: 14 }} />, color: "#374151", bg: "#F9FAFB", onClick: () => navigate(`/product/edit/${id}`), show: true },
                  { label: "Create PO", icon: <LocalShippingIcon sx={{ fontSize: 14 }} />, color: "#3B82F6", bg: "#EFF6FF", onClick: () => navigate("/bill/create"), show: true },
                  { label: "Print Label", icon: <PrintIcon sx={{ fontSize: 14 }} />, color: "#059669", bg: "#F0FDF4", onClick: () => { setTab("overview"); setPrintLabel(true); }, show: true },
                ].filter(a => a.show).map((action, i) => (
                  <Button key={i} fullWidth size="small" startIcon={action.icon} onClick={action.onClick}
                    sx={{
                      justifyContent: "flex-start", borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
                      bgcolor: action.bg, color: action.color, border: "none", mb: 0.75, py: 0.75,
                      "&:hover": { opacity: 0.85 },
                    }}>
                    {action.label}
                  </Button>
                ))}
              </Box>

              {/* Alerts */}
              {(() => {
                const alerts: { msg: string; type: "error" | "warning" | "info" }[] = [];
                if (tracked && stock <= 0) alerts.push({ msg: "Out of stock", type: "error" });
                else if (tracked && stock <= reorderPoint) alerts.push({ msg: "Below reorder point", type: "warning" });
                if (historyData?.ai_insights?.days_of_stock && historyData.ai_insights.days_of_stock < 5) alerts.push({ msg: `Only ${historyData.ai_insights.days_of_stock} days of stock left`, type: "warning" });
                if ((product?.supplier_mappings?.length || 0) === 0 && (historyData?.suppliers?.length || 0) === 0) alerts.push({ msg: "No supplier linked", type: "info" });

                if (alerts.length === 0) return null;
                const colors = { error: { bg: "#FEF2F2", border: "#FECACA", color: "#DC2626", icon: <ErrorIcon sx={{ fontSize: 14 }} /> }, warning: { bg: "#FFFBEB", border: "#FDE68A", color: "#D97706", icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> }, info: { bg: "#EFF6FF", border: "#BFDBFE", color: "#2563EB", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> } };

                return (
                  <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 2.5 }}>
                    <Typography fontSize={12} fontWeight={600} color="#111827" mb={1.5}>Alerts</Typography>
                    {alerts.map((a, i) => {
                      const c = colors[a.type];
                      return (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, mb: 0.75, borderRadius: 1.5, bgcolor: c.bg, border: `1px solid ${c.border}` }}>
                          <Box sx={{ color: c.color }}>{c.icon}</Box>
                          <Typography fontSize={11} fontWeight={600} color={c.color}>{a.msg}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}

              {/* Activity Feed */}
              {historyData?.movements && historyData.movements.length > 0 && (
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 2.5 }}>
                  <Typography fontSize={12} fontWeight={600} color="#111827" mb={1.5}>Recent Activity</Typography>
                  {historyData.movements.slice(0, 5).map((m: any, i: number) => (
                    <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.5, "&:last-child": { mb: 0 } }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: m.qty > 0 ? "#059669" : "#DC2626", mt: 0.75, flexShrink: 0 }} />
                      <Box>
                        <Typography fontSize={11} color="#374151" fontWeight={500}>
                          {m.qty > 0 ? "+" : ""}{m.qty} — {m.type.replace(/_/g, " ")}
                        </Typography>
                        <Typography fontSize={10} color="#9CA3AF">{m.reference || m.notes || new Date(m.date).toLocaleDateString()}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Status */}
              <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", p: 2.5 }}>
                <Typography fontSize={12} fontWeight={600} color="#111827" mb={1.5}>Status</Typography>
                {[
                  { label: "Active", value: product.isActive ? "Yes" : "No" },
                  { label: "Online", value: product.productViewOnline ? "Yes" : "No" },
                  { label: "POS", value: product.productViewPos ? "Yes" : "No" },
                  { label: "Tracking", value: tracked ? "Enabled" : "Disabled" },
                  { label: "Weight-Based", value: product.is_weight_based ? "Yes" : "No" },
                  { label: "Tax", value: product.tax_name ? `${product.tax_name} ${product.tax_rate}%` : "—" },
                ].map((s, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
                    <Typography fontSize={12} color="#6B7280">{s.label}</Typography>
                    <Typography fontSize={12} fontWeight={600} color={s.value === "Yes" || s.value === "Enabled" ? "#059669" : "#9CA3AF"}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>

            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Index;
